import dayjs, {Dayjs} from "dayjs";
import React, {forwardRef, useImperativeHandle, useMemo, useState} from "react";

interface Parameter {
	value?: string;
	minDate: string;
	maxDate: string;
	onMonthChange?: (date: Dayjs) => void;
	onDateChange: (date: Dayjs | null, e?: React.ChangeEvent<HTMLElement>) => void;
	data?: any;
}

interface Calendar {
	min: Dayjs;
	max: Dayjs;
	rangeMonth: Dayjs[];
	dayList: Dayjs[][];
	month: Dayjs;
	isNext: boolean;
	isPrevious: boolean;
	date: null | Dayjs;
	dateChange: (day: Dayjs, e?: React.ChangeEvent<HTMLElement>) => void;
	monthChange: (day: string) => void;
	clearDate: () => void;
	nextMonth: () => void;
	prevMonth: () => void;
}

type WithCalendarRef = Pick<
	Calendar,
	| "max"
	| "min"
	| "isNext"
	| "isPrevious"
	| "clearDate"
	| "nextMonth"
	| "prevMonth"
	| "date"
	| "month"
	| "dateChange"
	| "monthChange"
>;

const makeDayList = (referenceDate: Dayjs): Array<Dayjs | null> => {
	const dayCount = referenceDate.date(1).daysInMonth();
	/** 1~7 월~일 */
	const startWeekDay = referenceDate.day();
	const endWeekDay = referenceDate.date(dayCount).day();
	const firstWeekEmptyArray = Array(startWeekDay).fill(null);
	const lastWeekEmptyArray = Array(6 - endWeekDay).fill(null);
	const dayArray = Array(dayCount)
		.fill(0)
		.map((o, index) => {
			const day = referenceDate.add(index, "day");
			return day;
		});
	return [...firstWeekEmptyArray, ...dayArray, ...lastWeekEmptyArray];
};

const withCalendar = WrappedComponent =>
	forwardRef<WithCalendarRef, Parameter>((props, ref) => {
		const {minDate, maxDate, value, data} = props;

		const min = useMemo(() => {
			if (!dayjs(minDate).isValid()) return dayjs();
			return dayjs(minDate);
		}, [minDate]);

		const max = useMemo(() => {
			if (!dayjs(maxDate).isValid()) return min.add(12, "month");
			return dayjs(maxDate);
		}, [maxDate]);

		const [month, setMonth] = useState(() => {
			if (value) return dayjs(value);
			return min;
		});
		const [date, setDate] = useState(() => {
			if (value) return dayjs(value);
			return null;
		});

		const dayList = useMemo(() => {
			const days = makeDayList(month.date(1));
			return Array.from({length: days.length / 7}).map(() => days.splice(0, 7));
		}, [month]);

		const rangeMonth = useMemo(() => {
			const startMM = dayjs(minDate).set("date", 1);
			const endMM = dayjs(maxDate).set("date", 1);
			const diff = endMM.diff(startMM, "month") + 1;
			return Array.from({length: diff}).map((v, i) => startMM.add(i, "month"));
		}, [min, max]);

		/** 월 변경 */
		const monthChange = (day: string) => {
			const _day = dayjs(day);
			_day.isValid() && setMonth(_day);
			if (props.onMonthChange) props.onMonthChange(_day);
		};

		/** 다음 달 여부 */
		const isNext = !month.isSame(max, "month");

		/** 이전 달 여부 */
		const isPrevious = !month.isSame(min, "month");

		/** 다음 달 */
		const nextMonth = () => isNext && monthChange(month.add(1, "month").format("YYYYMM"));

		/** 이전 달 */
		const prevMonth = () => isPrevious && monthChange(month.add(-1, "month").format("YYYYMM"));

		const dateChange = (day: Dayjs, e?: React.ChangeEvent<HTMLElement>) => {
			setDate(day);
			props.onDateChange(day, e);
		};
		const clearDate = () => {
			setDate(null);
			props.onDateChange(null);
		};

		useImperativeHandle(ref, () => ({
			min,
			max,
			month,
			date,
			isNext,
			isPrevious,
			dateChange,
			clearDate,
			monthChange,
			nextMonth,
			prevMonth,
		}));

		const wrapProps = {
			min,
			max,
			isNext,
			isPrevious,
			rangeMonth,
			dayList,
			month,
			date,
			dateChange,
			clearDate,
			monthChange,
			nextMonth,
			prevMonth,
			data,
		};

		return (
			<>
				<WrappedComponent {...wrapProps} />
			</>
		);
	});

export default withCalendar;
export type {Calendar, Parameter, WithCalendarRef};
